import { useState, useEffect } from 'react';

interface SponsorshipModalProps {
  open: boolean;
  onClose: () => void;
}

export default function SponsorshipModal({ open, onClose }: SponsorshipModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      setSubmitted(false);
      setError('');
      setCharCount(0);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) {
      setError('A mensagem não pode ultrapassar 500 caracteres.');
      return;
    }
    setLoading(true);
    setError('');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    Array.from(form.elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name && input.value) data.append(input.name, input.value);
    });
    try {
      const res = await fetch('https://readdy.ai/api/form/d72k08kjuufb4jdbl5k0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Ocorreu um erro. Tente novamente.');
      }
    } catch {
      setError('Ocorreu um erro. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-obsidian/92 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-obsidian border border-gold/30 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center text-cream/50 hover:text-gold transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-xl" />
        </button>

        <div className="p-8 md:p-10">
          {/* Header */}
          <div className="mb-8">
            <span className="font-sans text-xs tracking-widest uppercase text-gold block mb-3" style={{ letterSpacing: '0.25em' }}>
              Patrocínio
            </span>
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-cream leading-tight">
              Leve sua Marca às Leoas
            </h2>
            <div className="w-10 h-px bg-gold mt-4" />
          </div>

          {submitted ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 flex items-center justify-center border border-gold mx-auto mb-5">
                <i className="ri-check-line text-gold text-2xl" />
              </div>
              <h3 className="font-serif text-xl font-bold text-cream mb-3">Proposta recebida!</h3>
              <p className="font-sans text-sm text-cream/60 font-light leading-relaxed">
                Recebemos sua solicitação de patrocínio. Nossa equipe entrará em contato com uma proposta personalizada em até 48 horas.
              </p>
              <button
                onClick={onClose}
                className="mt-8 font-sans text-xs tracking-widest uppercase border border-gold/60 text-gold px-8 py-3 hover:bg-gold/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ letterSpacing: '0.2em' }}
              >
                Fechar
              </button>
            </div>
          ) : (
            <form
              data-readdy-form
              id="sponsorship-form"
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div>
                <label className="font-sans text-xs tracking-widest uppercase text-cream/50 block mb-2" style={{ letterSpacing: '0.15em' }}>
                  Nome Completo *
                </label>
                <input
                  type="text"
                  name="nome"
                  required
                  placeholder="Seu nome completo"
                  className="w-full bg-transparent border border-gold/20 focus:border-gold text-cream text-sm px-4 py-3 outline-none transition-colors placeholder:text-cream/20 font-light"
                />
              </div>

              <div>
                <label className="font-sans text-xs tracking-widest uppercase text-cream/50 block mb-2" style={{ letterSpacing: '0.15em' }}>
                  E-mail Corporativo *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="seu@empresa.com"
                  className="w-full bg-transparent border border-gold/20 focus:border-gold text-cream text-sm px-4 py-3 outline-none transition-colors placeholder:text-cream/20 font-light"
                />
              </div>

              <div>
                <label className="font-sans text-xs tracking-widest uppercase text-cream/50 block mb-2" style={{ letterSpacing: '0.15em' }}>
                  Empresa / Marca
                </label>
                <input
                  type="text"
                  name="empresa"
                  placeholder="Nome da empresa"
                  className="w-full bg-transparent border border-gold/20 focus:border-gold text-cream text-sm px-4 py-3 outline-none transition-colors placeholder:text-cream/20 font-light"
                />
              </div>

              <div>
                <label className="font-sans text-xs tracking-widest uppercase text-cream/50 block mb-2" style={{ letterSpacing: '0.15em' }}>
                  Cota de Interesse
                </label>
                <select
                  name="cota"
                  className="w-full bg-obsidian border border-gold/20 focus:border-gold text-cream text-sm px-4 py-3 outline-none transition-colors font-light cursor-pointer"
                >
                  <option value="">Selecione uma cota</option>
                  <option value="Diamante">Diamante — Naming Rights (1 cota)</option>
                  <option value="Ouro">Ouro — Logo em Destaque (3 cotas)</option>
                  <option value="Prata">Prata — Logo Digital (5 cotas)</option>
                  <option value="Apoio">Apoio — Logo Site</option>
                </select>
              </div>

              <div>
                <label className="font-sans text-xs tracking-widest uppercase text-cream/50 block mb-2" style={{ letterSpacing: '0.15em' }}>
                  Mensagem
                </label>
                <textarea
                  name="mensagem"
                  rows={4}
                  maxLength={500}
                  placeholder="Conte-nos sobre seus objetivos com o patrocínio..."
                  className="w-full bg-transparent border border-gold/20 focus:border-gold text-cream text-sm px-4 py-3 outline-none transition-colors placeholder:text-cream/20 font-light resize-none"
                  onChange={(e) => setCharCount(e.target.value.length)}
                />
                <p className={`font-sans text-xs mt-1 text-right ${charCount > 500 ? 'text-wine-light' : 'text-cream/30'}`}>
                  {charCount}/500
                </p>
              </div>

              {error && (
                <p className="font-sans text-xs text-wine-light">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading || charCount > 500}
                className="w-full font-sans text-xs tracking-widest uppercase bg-gold text-obsidian py-4 hover:bg-gold-dark transition-all duration-300 cursor-pointer whitespace-nowrap font-semibold disabled:opacity-60 mt-2"
                style={{ letterSpacing: '0.2em' }}
              >
                {loading ? 'Enviando...' : 'Quero Patrocinar'}
              </button>

              <p className="font-sans text-xs text-cream/30 text-center font-light">
                Resposta garantida em até 48 horas úteis.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
